<template>
	<v-dialog v-if="!token" v-model="model" max-width="600">
		<template #activator="{ props }">
			<v-btn
				v-bind="props"
				id="login-button"
				class="bg-secondary"
				elevation="2"
			>
				<span class="login mr-1">Log in</span>
				<v-icon size="small">mdi-login-variant</v-icon>
			</v-btn>
		</template>

		<v-card class="pb-5" :disabled="!!loading" :loading="loading">
			<v-toolbar color="primary" dark flat>
				<v-toolbar-title>Log in</v-toolbar-title>
				<v-spacer />
				<v-tooltip location="bottom">
					<template #activator="{ props }">
						<v-btn v-bind="props" icon size="large" @click="close">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</template>
					<span>Close</span>
				</v-tooltip>
			</v-toolbar>
			<v-card-text class="pa-10">
				<v-form @submit.prevent.stop>
					<v-text-field
						v-model.trim="email"
						class="mb-2"
						:error-messages="emailErrors"
						label="Email address"
						name="email"
						prepend-icon="mdi-account"
						autocomplete="off"
						required
						autofocus
						@update:model-value="emailState"
						@blur="v$.email.$touch()"
						@keyup.enter="handleEmailEnter"
					/>

					<v-row v-show="ssoAvailable" justify="start">
						<v-col class="mr-3 my-2 flex-grow-0" style="width: 24px">
							<v-icon>mdi-lock</v-icon>
						</v-col>

						<v-col align-self="center">
							SSO sign-in is enabled for this domain
						</v-col>
					</v-row>

					<v-text-field
						v-show="!ssoAvailable"
						id="password"
						ref="passwordField"
						v-model="password"
						class="mb-2"
						:error-messages="passwordErrors"
						:append-inner-icon="reveal ? 'mdi-eye' : 'mdi-eye-off'"
						:type="reveal ? 'text' : 'password'"
						prepend-icon="mdi-lock"
						counter
						label="Password"
						name="password"
						required
						@click:append="reveal = !reveal"
						@update:model-value="passwordState"
						@blur="v$.password.$touch()"
						@keyup.enter="login()"
					/>
				</v-form>

				<div class="d-flex align-center justify-space-between" height="20px">
					<div class="d-flex flex-column align-start ga-1">
						<a
							v-if="!ssoAvailable"
							class="text-primary text-subtitle-2"
							href="#"
							@click.prevent="forgotPassword"
						>
							Forgot your password?
						</a>
					</div>
					<v-checkbox
						v-model="rememberEmail"
						class="remember-checkbox text-caption"
						label="Remember me"
						density="compact"
						hide-details
						@update:model-value="onRememberToggle"
					/>
				</div>
			</v-card-text>

			<v-card-actions>
				<v-spacer />

				<v-btn
					v-if="ssoAvailable"
					elevation="2"
					class="bg-secondary"
					@click="sendSsoRequest"
				>
					Continue
				</v-btn>

				<template v-else>
					<v-btn
						class="me-3"
						variant="outlined"
						color="primary"
						prepend-icon="mdi-creation"
						@click="requestMagicLink"
					>
						Login via Magic Link
					</v-btn>

					<v-btn class="bg-secondary" elevation="2" @click="login()">
						Log in
					</v-btn>
				</template>

				<v-spacer />
			</v-card-actions>
		</v-card>
	</v-dialog>
	<form
		ref="samlForm"
		method="post"
		:action="entityEndpoint"
		autocomplete="off"
	>
		<input type="hidden" :name="type" :value="context" />
		<input type="hidden" name="RelayState" :value="relayState" />
		<input type="hidden" name="Id" :value="id" />
		<input type="hidden" name="Email" :value="email" />
		<input type="hidden" name="Redirect" :value="redirect" />
	</form>
</template>

<script>
import * as Sentry from "@sentry/vue";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import { required, email, minLength, maxLength } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import auth from "@/auth";
import CookieUtils from "@/utils/cookies";
import { safeRedirectTarget } from "@/utils/redirect";

const ssoDomainRegexes = [/^.+@humanpredictions[.]com$/];

export default {
	name: "VLogin",
	props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["activate-activity-timer", "update:modelValue"],

	setup() {
		return {
			v$: useVuelidate(),
		};
	},

	data() {
		return {
			context: "",
			email: "",
			entityEndpoint: "",
			id: "",
			invalidEmail: false,
			invalidPassword: false,
			loading: false,
			notification: {
				message: "",
				status: "",
			},
			password: "",
			redirect: "",
			relayState: "",
			rememberEmail: false,
			reveal: false,
			ssoAvailable: false,
			token: this.$route?.query?.token,
			type: "",
		};
	},

	computed: {
		model: {
			get() {
				return this.modelValue;
			},
			set(val) {
				this.$emit("update:modelValue", val);
			},
		},
		emailErrors() {
			if (!this.v$.email.$dirty) {
				return [];
			}

			return this.v$.email.$errors.map((error) => {
				switch (error.$validator) {
					case "required":
						return "E-mail address is required";
					case "invalid":
						return "E-mail address not found";
					case "email":
						return "Invalid e-mail address";
					case "maxLength":
						return "E-mail address is too long";
					default:
						return "Invalid e-mail address";
				}
			});
		},
		passwordErrors() {
			if (!this.v$.password.$dirty) {
				return [];
			}

			return this.v$.password.$errors.map((error) => {
				switch (error.$validator) {
					case "required":
						return "Password is required";
					case "invalid":
						return "Invalid password";
					case "minLength":
						return "Password is too short";
					case "maxLength":
						return "Password is too long";
					default:
						return "Invalid password";
				}
			});
		},
	},

	watch: {
		modelValue(val) {
			if (val === false) {
				this.clear();
			} else {
				this.v$.$reset();
			}
		},
	},

	mounted() {
		if (!this.token) {
			this.model = true;

			// fallback to local auth flow setup
			const rememberFlag = localStorage.getItem("rememberEmail") ?? "0";
			this.rememberEmail = rememberFlag === "1";

			if (this.rememberEmail) {
				const saved = localStorage.getItem("rememberedEmail");
				if (typeof saved === "string" && saved.length > 0) {
					this.email = saved;
					this.emailState();
				}
			}
		}
	},

	methods: {
		...mapActions(useMainStore, ["setUser", "setAuthenticated", "showNotification"]),
		clear() {
			this.password = "";

			this.invalidEmail = false;
			this.invalidPassword = false;

			this.ssoAvailable = false;
			this.context = "";
			this.entityEndpoint = "";
			this.id = "";
			this.relayState = "";
			this.token = "";
			this.type = "";
		},
		close() {
			this.model = false;
		},
		sendSsoRequest() {
			this.loading = "secondary";

			const email = this.email.toLowerCase().trim();
			if (email && this.rememberEmail) {
				localStorage.setItem("rememberedEmail", email);
			}

			this.$http
				.post("/sso-login", {
					redirect:
						typeof this.$route?.query?.redirect === "string"
							? this.$route.query.redirect
							: undefined,
				})
				.then(({ data }) => {
					// redirect to idp login
					this.context = data.context;
					this.entityEndpoint = data.entityEndpoint;
					this.id = data.id;
					this.redirect = this.$route?.query?.redirect;
					this.relayState = data.relayState;
					this.type = data.type;

					setTimeout(() => {
						this.$refs.samlForm.submit();

						this.context = "";
						this.entityEndpoint = "";
						this.id = "";
						this.redirect = "";
						this.relayState = "";
						this.type = "";
					}, 1);
				})
				.catch((error) => {
					auth.clearAuthenticated();
					this.setAuthenticated(false);

					console.error(error);
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.notify(error.response.data);
						} else {
							this.notify(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		login(token) {
			if (token || !this.v$.$invalid) {
				this.loading = "secondary";

				const email = this.email.toLowerCase().trim();
				if (email && !token && this.rememberEmail) {
					localStorage.setItem("rememberedEmail", email);
				}

				this.$http
					.post("/login", {
						email: token ? undefined : email,
						password: this.password,
						token,
					})
					.then((response) => {
						this.setAuthenticated(auth.isAuthenticated());

						const payload = response.data.payload;

						// if this was an SSO login (token-based), enable auto-login for next time
						// skip in development
						if (token && import.meta.env.VUE_APP_ENV !== "development") {
							try {
								CookieUtils.setAutoLogin();
							} catch (e) {
								/* ignore cookie errors */
							}
						}

						// activate activity tracker
						this.$emit("activate-activity-timer");

						// register logged in user
						this.setUser(payload.user);

						// set sentry user
						Sentry.setUser({ email: payload.user.email });

						// Identify user to LaunchDarkly
						if (this.$ldClient) {
							this.$ldClient.identify({
								kind: "user",
								key: payload.user.uuid,
								email: payload.user.email,
								firstName: payload.user.first_name,
								lastName: payload.user.last_name,
								name: `${payload.user.first_name} ${payload.user.last_name}`,
							});
						}

						// display success
						this.notify(response.data);

						// redirect to intended route if present, else home
						const target = safeRedirectTarget(this.$route?.query?.redirect);
						if (target) {
							this.$router.replace(target);
						} else {
							this.$router.push("/home");
						}

						// close the dialog
						this.close();
					})
					.catch((error) => {
						// clear cookie
						auth.clearAuthenticated();
						this.setAuthenticated(false);

						// clear sentry user
						Sentry.setUser(null);

						// display error
						if (error.response) {
							if (error.response.data.message) {
								this.notify(error.response.data);
							} else {
								this.notify(error.response.data.error);
							}

							if (error.response.data.code) {
								switch (error.response.data.code) {
									case "E_INVALID_PASSWORD":
										// invalidate password field
										this.invalidPassword = true;
										this.v$.password.$touch();
										break;
									case "E_USER_NOT_FOUND":
										// invalidate email field
										this.invalidEmail = true;
										this.v$.email.$touch();
										break;
									case "E_NO_USER_ROLES":
										// close dialog
										this.close();
										break;
								}
							}
						}
					})
					.finally(() => {
						this.loading = false;
					});
			} else {
				// set all to dirty
				this.v$.$touch();
			}
		},
		notify(data) {
			this.showNotification({
				message: data.message,
				status: data.status,
			});
		},
		emailState() {
			if (this.invalidEmail === true) {
				this.invalidEmail = false;
				this.v$.email.$touch();
			}

			// toggle between sso authentication and password
			if (this.isSsoAvailable()) {
				this.ssoAvailable = true;
			} else {
				this.ssoAvailable = false;
			}
		},
		handleEmailEnter() {
			// if email is for SSO domain, send SSO request
			if (this.ssoAvailable) {
				this.sendSsoRequest();
			}
			// if email is provided but not SSO, and password is filled, login
			else if (this.email && this.password) {
				this.login();
			}
			// if email is provided but not SSO, and no password, focus password field and show validation
			else if (this.email && !this.password) {
				this.v$.password.$touch();

				// focus the password field
				this.$nextTick(() => {
					// vuetify components expose the underlying input via focus() method
					this.$refs.passwordField?.focus();
				});
			}
		},
		isSsoAvailable() {
			return ssoDomainRegexes.some((regex) => regex.test(this.email));
		},
		passwordState() {
			if (this.invalidPassword === true) {
				this.invalidPassword = false;
				this.v$.password.$touch();
			}
		},
		forgotPassword() {
			if (this.v$.email.$invalid) {
				this.v$.email.$touch();

				return;
			}

			this.loading = "secondary";

			this.$http
				.post("/forgot-password", {
					email: this.email.toLowerCase(),
				})
				.then((response) => {
					// display success
					this.notify(response.data);

					// close the dialog
					this.close();
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.notify(error.response.data);
						} else {
							this.notify(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		requestMagicLink() {
			// Passwordless login for admin-provisioned prospects (DCTL-1927).
			// Silent-success (mirrors forgotPassword): the response never reveals
			// whether the email is registered, and the link is sent on demand.
			if (this.v$.email.$invalid) {
				this.v$.email.$touch();

				return;
			}

			this.loading = "secondary";

			this.$http
				.post("/magic-link-request", {
					email: this.email.toLowerCase(),
					// carry the originally-requested destination (e.g. the
					// provenance link clicked) so the emailed magic link lands
					// the prospect there after sign-in (mirrors sendSsoRequest)
					redirect:
						typeof this.$route?.query?.redirect === "string"
							? this.$route.query.redirect
							: undefined,
					// "Remember me": persisted on the token row server-side so the
					// resulting login establishes a 30-day persistent session for
					// the prospect (DCTL-1951)
					remember: this.rememberEmail,
				})
				.then((response) => {
					// display the "check your email" confirmation
					this.notify(response.data);

					// close the dialog
					this.close();
				})
				.catch((error) => {
					// display error
					if (error.response) {
						if (error.response.data.message) {
							this.notify(error.response.data);
						} else {
							this.notify(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		onRememberToggle() {
			if (!this.rememberEmail) {
				localStorage.setItem("rememberEmail", "0");
				localStorage.removeItem("rememberedEmail");
			} else {
				localStorage.setItem("rememberEmail", "1");
			}
		},
	},

	validations: {
		email: {
			required,
			email,
			maxLength: maxLength(254),
			invalid() {
				return !this.invalidEmail;
			},
		},
		password: {
			required,
			minLength: minLength(8),
			maxLength: maxLength(60),
			invalid() {
				return !this.invalidPassword;
			},
		},
	},
};
</script>

<style scoped lang="scss">
.login {
	line-height: 36px;
}
</style>
